import { Images } from '@styled-icons/boxicons-regular';
import {
  backgroundColors,
  backgroundColorsBasic,
  CLEAR_COLOR_NAME,
  colorsGradient,
  DEFAULT_ACCENT_NAME,
  RootElement,
} from '@typedream/core';
import { setNodes } from '@udecode/plate-core';
import { pick } from 'lodash';
import {
  useEffect, useState,
} from 'react';
import shallow from 'zustand/shallow';
import BackgroundSettings from '../../../../components/BackgroundSettings';
import { useTPlateEditorRef } from '../../../../plate';
import { useDreamEditorStore } from '../../../../plugins/store';
import { useOutsideEventWithCanvas } from '../../../../utils/eventAlerter';
import styles from './PageSettings.module.css';
import { Portal } from '../../../../components/Portal';
import { IFRAME_CONTENT_OVERLAY_ID } from '../../../../components/IframeContentOverlay';
import { ToolbarNavbarContainerInsideDropdownEvent } from '../../../../types';

const colorOptions = {
  [CLEAR_COLOR_NAME]: 'var(--background)',
  [DEFAULT_ACCENT_NAME]: backgroundColors[DEFAULT_ACCENT_NAME],
  ...backgroundColorsBasic,
  ...colorsGradient,
};

function Settings() {
  const editor = useTPlateEditorRef();

  const rootProperties = useDreamEditorStore(() => {
    const root = editor.children[0];
    return pick(root, [
      'background',
      'backgroundImage',
      'backgroundDarken',
      'backgroundVideo',
    ]) as Pick<
    RootElement,
    'background' | 'backgroundImage' | 'backgroundDarken' | 'backgroundVideo'
    >;
  }, shallow);

  const setBackgroundColor = (background: string) => {
    setNodes(
      editor,
      {
        background: background.trim(),
        backgroundImage: undefined,
        backgroundVideo: undefined,
        backgroundDarken: undefined,
      },
      { at: [0] },
    );
  };

  const setBackgroundImage = (url: string) => {
    setNodes(
      editor,
      {
        background: undefined,
        backgroundImage: url,
        backgroundVideo: undefined,
      },
      { at: [0] },
    );
  };

  const setBackgroundVideo = (url: string) => {
    setNodes(
      editor,
      {
        background: undefined,
        backgroundImage: undefined,
        backgroundVideo: url,
      },
      { at: [0] },
    );
  };

  const removeBackgroundMedia = () => {
    setNodes(
      editor,
      {
        background: 'default',
        backgroundImage: undefined,
        backgroundVideo: undefined,
        backgroundDarken: undefined,
      },
      { at: [0] },
    );
  };

  const toggleBackgroundDarken = () => {
    setNodes(
      editor,
      { backgroundDarken: !rootProperties.backgroundDarken },
      { at: [0] },
    );
  };

  return (
    <div className={styles.card}>
      <h4 className={styles.cardHeader}>Page Settings</h4>
      <div>
        <span className={styles.cardSubHeader}>Background</span>
        <div className={styles.tabs}>
          <BackgroundSettings
            currentValues={rootProperties}
            colorOptions={colorOptions}
            onSelectColor={setBackgroundColor}
            onSelectImage={setBackgroundImage}
            onSelectVideo={setBackgroundVideo}
            onToggleDarken={toggleBackgroundDarken}
            onRemoveBackgroundMedia={removeBackgroundMedia}
          />
        </div>
      </div>
    </div>
  );
}

function PortalWrapper({ children }: { children: React.ReactNode }) {
  const [containerElement, setContainerElement] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setTimeout(() => {
      const container = document.getElementById(IFRAME_CONTENT_OVERLAY_ID);
      setContainerElement(container);
    }, 0);
  }, []);

  // eslint-disable-next-line react/jsx-no-useless-fragment
  if (!containerElement) return <>{children}</>;

  return (
    <Portal container={containerElement}>
      {children}
    </Portal>
  );
}

function PageSettings() {
  const editor = useTPlateEditorRef();

  const [open, setOpen] = useState(false);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const onInsideDropdownMenuhandler = ({ isInside }: ToolbarNavbarContainerInsideDropdownEvent) => {
    setIsDropdownOpen(isInside);
  };
  useEffect(() => {
    editor.emitter.on('toolbar:toggle-navbar-container-inside-dropdown', onInsideDropdownMenuhandler);
    return () => {
      editor.emitter.off('toolbar:toggle-navbar-container-inside-dropdown', onInsideDropdownMenuhandler);
    };
  }, [editor.emitter]);

  const ref = useOutsideEventWithCanvas<HTMLDivElement>('mousedown', () => {
    setOpen(false);
  });

  return (
    <PortalWrapper>
      <div
        ref={ref}
        className={styles.container}
        style={{
          display: isDropdownOpen ? 'none' : undefined,
        }}
      >
        <button
          type="button"
          className={styles.button}
          onClick={() => setOpen((v) => !v)}
          data-state={open ? 'open' : ''}
        >
          <Images size={15} style={{ marginRight: 5 }} />
          Page Settings
        </button>
        {open && <Settings />}
      </div>
    </PortalWrapper>
  );
}

export default PageSettings;
